import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Cab } from "../../Global Icons/Cab.svg";
import TextInput from "../../Global Functions/textInput";
import SelectInput from "../../Global Functions/selectInput";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCountryListPagination,
  viewStateListPagination,
  viewCityList,
} from "../../Redux/Actions/settingsActions";
import {
  viewFinancePartner,
  updateFinancePartner,
} from "../../Redux/Actions/financePartnersActions";
import { ScrollTop } from "../../Utils/helpers";
import toast from "react-hot-toast";
import TwoButtons from "../../Global Functions/buttons";
import AddImage from "../../Global Functions/addImage";
import "../Franchise/addFranchise.css";
import { PhonecodeSelect } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";

const EditFinancePartners = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { countryList } = useSelector((state) => state.country || {});
  const { states } = useSelector((state) => state.states || {});
  const { cities } = useSelector((state) => state.city || {});
  const { user } = useSelector((state) => state.auth || {});
  const financePartnerDetails = useSelector(
    (state) => state.finance_partners.financePartner || {}
  );

  const [aboutError, setAboutError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isCountryLoaded, setIsCountryLoaded] = useState(false);
  const [isStateLoaded, setIsStateLoaded] = useState(false);

  const [formState, setFormState] = useState({
    company_name: "",
    logo: "",
    country_code: "",
    contact_number: "",
    address: "",
    about: "",
    country: "",
    state: "",
    city: "",
    status: "active",
  });

  useEffect(() => {
    ScrollTop();
    dispatch(viewCountryListPagination());
  }, [dispatch]);

  // Fetch partner data on mount
  useEffect(() => {
    if (id) {
      dispatch(viewFinancePartner(id));
    }
  }, [dispatch, id]);

  // Populate form fields initially (country only)
  useEffect(() => {
    if (financePartnerDetails && financePartnerDetails._id && !isLoaded) {
      setFormState({
        company_name: financePartnerDetails.company_name || "",
        logo: financePartnerDetails.logo || "",
        country_code: financePartnerDetails.country_code || "",
        contact_number: financePartnerDetails.contact_number || "",
        address: financePartnerDetails.address || "",
        about: financePartnerDetails.about || "",
        country: financePartnerDetails.country?._id || "",
        state: "",
        city: "",
        status: financePartnerDetails.status || "active",
      });
      setLogoPreview(financePartnerDetails.logo || "");
      setIsLoaded(true);
    }
  }, [financePartnerDetails, isLoaded]);

  // Fetch states when country is set (only once)
  useEffect(() => {
    if (isLoaded && formState.country && !isCountryLoaded) {
      dispatch(viewStateListPagination({ country: formState.country }));
      setIsCountryLoaded(true);
    }
  }, [formState.country, dispatch, isLoaded, isCountryLoaded]);

  // Set state field after states load, if not already set
  useEffect(() => {
    if (
      isLoaded &&
      isCountryLoaded &&
      Array.isArray(states?.data) &&
      states.data.length > 0 &&
      !formState.state &&
      financePartnerDetails?.state?._id
    ) {
      setFormState((prev) => ({
        ...prev,
        state: financePartnerDetails.state._id,
      }));
    }
  }, [states, isLoaded, isCountryLoaded, financePartnerDetails?.state, formState.state]);

  // Fetch cities when state is set (only once)
  useEffect(() => {
    if (isLoaded && formState.state && !isStateLoaded) {
      dispatch(viewCityList({ state: formState.state, page: 1, limit: 100 }));
      setIsStateLoaded(true);
    }
  }, [formState.state, dispatch, isLoaded, isStateLoaded]);

  // Set city field after cities load, if not already set
  useEffect(() => {
    if (
      isLoaded &&
      isStateLoaded &&
      Array.isArray(cities?.data) &&
      cities.data.length > 0 &&
      !formState.city &&
      financePartnerDetails?.city?._id
    ) {
      setFormState((prev) => ({
        ...prev,
        city: financePartnerDetails.city._id,
      }));
    }
  }, [cities, isLoaded, isStateLoaded, financePartnerDetails?.city, formState.city]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "contact_number") {
      if (/^\d*$/.test(value)) {
        setFormState((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === "about") {
      if (value.length <= 250) {
        setFormState((prev) => ({ ...prev, [name]: value }));
        setAboutError("");
      } else {
        setAboutError("Only 250 characters allowed");
      }
    } else if (name === "address") {
      if (value.length <= 250) {
        setFormState((prev) => ({ ...prev, [name]: value }));
        setAddressError("");
      } else {
        setAddressError("Only 250 characters allowed");
      }
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Please upload a .png, .jpg, or .jpeg image only."
      );
      event.target.value = "";
      return;
    }

    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, logo: uploadedUrl }));
        setLogoPreview(uploadedUrl);
      }
    } catch (error) {
      toast.error("Image upload failed");
    }
  };

  const handleSubmit = async () => {
    if (
      !formState.company_name ||
      !formState.logo ||
      !formState.country_code ||
      !formState.contact_number ||
      !formState.address ||
      !formState.country ||
      !formState.state ||
      !formState.city
    ) {
      return toast.error("Please fill all required fields.");
    }

    const payload = {
      ...formState,
      company_name_withoutspace: formState.company_name.replace(/\s+/g, ""),
      updated_by: user?.id || user?._id || "defaultAdminId",
      id: id,
    };

    try {
      const response = await dispatch(updateFinancePartner(payload));
      if (response.statusCode === 200) {
        // toast.success("Finance partner updated successfully");
        navigate("/finance_partners");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    }
  };

  return (
    <MainLayout>
      <div className="franchise-form-container">
        <div className="franchise-form-wrapper franchise-card">
          <div className="franchise-card-content">
            <div className="franchise-header">
              <div className="franchise-header-icon">
                <Cab />
              </div>
              <h1>Update Finance Partner</h1>
            </div>

            <div className="form-section">
              <TextInput
                label="Company Name"
                name="company_name"
                value={formState.company_name}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
              />
            </div>

            <div className="form-section">
              <label className="form-label file-upload-area" htmlFor="logo_input">
                Logo<span style={{ color: "red" }}>*</span>
                <input
                  id="logo_input"
                  type="file"
                  name="logo"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  className="form-input"
                />
              </label>
              {logoPreview && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: "8px" }}
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label className="form-label">Country Code *</label>
              <PhonecodeSelect
                placeHolder="Select Country Code"
                value={formState.country_code || ""}
                onChange={(country) => {
                  if (country && country.phone_code) {
                    const codeWithPlus = country.phone_code.startsWith("+")
                      ? country.phone_code
                      : "+" + country.phone_code;
                    setFormState((prev) => ({
                      ...prev,
                      country_code: codeWithPlus,
                    }));
                  } else {
                    setFormState((prev) => ({ ...prev, country_code: "" }));
                  }
                }}
                disabled={false}
              />
            </div>

            <div className="form-section">
              <TextInput
                label="Contact Number"
                name="contact_number"
                value={formState.contact_number}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
              />
            </div>

            <div className="form-section">
              <TextInput
                label="Address"
                name="address"
                value={formState.address}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
              />
              {addressError && <p style={{ color: "red", marginTop: "5px" }}>{addressError}</p>}
            </div>

            <div className="form-section">
              <TextInput
                label="About"
                name="about"
                value={formState.about}
                onChange={handleChange}
                inputClass="form-input"
                labelClass="form-label"
                textarea
                rows={4}
              />
              {aboutError && <p style={{ color: "red", marginTop: "5px" }}>{aboutError}</p>}
            </div>

            <div className="form-section form-grid-3">
              <SelectInput
                label="Country"
                name="country"
                value={formState.country}
                onChange={handleChange}
                required
                options={
                  Array.isArray(countryList?.data)
                    ? countryList.data.map((c) => ({
                        label: c.country_name,
                        value: c._id,
                      }))
                    : []
                }
                selectClass="form-input"
                labelClass="form-label"
              />
              <SelectInput
                label="State"
                name="state"
                value={formState.state}
                onChange={handleChange}
                required
                disabled={!formState.country}
                options={
                  Array.isArray(states?.data)
                    ? states.data.map((s) => ({
                        label: s.state_name,
                        value: s._id,
                      }))
                    : []
                }
                selectClass="form-input"
                labelClass="form-label"
              />
              <SelectInput
                label="City"
                name="city"
                value={formState.city}
                onChange={handleChange}
                required
                disabled={!formState.state}
                options={
                  Array.isArray(cities?.data)
                    ? cities.data.map((c) => ({
                        label: c.city_name,
                        value: c._id,
                      }))
                    : []
                }
                selectClass="form-input"
                labelClass="form-label"
              />
            </div>

            <div className="form-navigation">
              <TwoButtons
                buttonHead1="Cancel"
                noUrl={true}
                LinkURL="/finance_partners"
                onClick2={handleSubmit}
                buttonHead2="Update"
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditFinancePartners;
